/**
 * Is this link the page you're on? (STATES_AND_INTERACTIVE_ELEMENTS_PLAN §13.3)
 *
 * One answer for every renderer — Budflow, both storefronts and the page
 * runtime — so it is framework-free and has no imports. Storefront copies are
 * hand-mirrored; change them together.
 *
 * - `'page'`: the link goes to this page → `aria-current="page"`.
 * - `'true'`: the link goes to a section this page is inside (`/blog` while on
 *   `/blog/x`) → `aria-current="true"`. The home page is never a section.
 * - `null`: neither, or not a page link at all.
 *
 * Rules: letter case and a trailing slash don't matter; a full URL on the
 * site's own host counts as internal; a redirect is followed, so a link to a
 * page's old address still matches it. A link with a query or a `#` is a view
 * or a place on a page, never "the page", so it never matches.
 */
export type CurrentMatch = 'page' | 'true' | null;

export interface MatchPathOptions {
  /** "This page only": an ancestor match is not reported. */
  exactOnly?: boolean;
  /** The site's own hosts (`goembarc.com`); a full URL on one is internal. */
  siteHosts?: readonly string[];
  /** Website redirects, `source path → destination path`. */
  redirects?: Readonly<Record<string, string>>;
}

const MAX_REDIRECT_HOPS = 5;

/** A path in its comparable form: lower case, one slash, none at the end. */
export function normalisePath(path: string): string {
  let p = path;
  try {
    p = decodeURI(p);
  } catch {
    // A malformed escape stays as written; it still compares to itself.
  }
  p = p.toLowerCase().replace(/\/{2,}/g, '/');
  if (!p.startsWith('/')) p = '/' + p;
  if (p.length > 1) p = p.replace(/\/+$/, '');
  return p || '/';
}

/**
 * The page path a link goes to, or null when it is not a link to a page on
 * this site (another site, `mailto:`, `tel:`, a bare `#`, a query or hash).
 */
export function linkPagePath(href: string | undefined | null, siteHosts: readonly string[] = []): string | null {
  if (!href) return null;
  const raw = href.trim();
  if (!raw || raw.startsWith('#')) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) && !/^https?:/i.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw, 'http://site.invalid');
  } catch {
    return null;
  }
  if (url.host !== 'site.invalid') {
    const host = url.host.toLowerCase().replace(/^www\./, '');
    const own = siteHosts.some((h) => h.toLowerCase().replace(/^www\./, '') === host);
    if (!own) return null;
  } else if (raw.startsWith('//')) {
    return null;
  }
  if (url.search || url.hash) return null;
  return normalisePath(url.pathname);
}

function followRedirects(path: string, redirects: Readonly<Record<string, string>> | undefined): string {
  if (!redirects) return path;
  let current = path;
  const seen = new Set<string>([current]);
  for (let i = 0; i < MAX_REDIRECT_HOPS; i++) {
    const next = redirects[current];
    if (next === undefined) break;
    const target = linkPagePath(next);
    if (target === null || seen.has(target)) break;
    seen.add(target);
    current = target;
  }
  return current;
}

/** Normalises redirect keys once, so lookups compare like with like. */
export function normaliseRedirects(map: Readonly<Record<string, string>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [from, to] of Object.entries(map)) out[normalisePath(from)] = to;
  return out;
}

export function matchPath(
  href: string | undefined | null,
  currentPath: string | undefined | null,
  options: MatchPathOptions = {},
): CurrentMatch {
  if (currentPath == null) return null;
  const linked = linkPagePath(href, options.siteHosts);
  if (linked === null) return null;
  const target = followRedirects(linked, options.redirects);
  const here = normalisePath(currentPath.split(/[?#]/)[0] || '/');
  if (target === here) return 'page';
  if (options.exactOnly || target === '/') return null;
  return here.startsWith(target + '/') ? 'true' : null;
}
